/**
 * sl-bpu6.2.1: useWorkspaceAppBases — SWR hook for the list-view page.
 *
 * Wraps the `appBases(workspaceId)` query. Unlike the global
 * {@link useQuery} wrapper this variant does NOT use suspense — the list-page
 * renders its own skeleton while `isLoading` is true.
 *
 * Cache key `['appBases', workspaceId]` is stable so card mutations can
 * revalidate via `mutate()`. Workspace metadata events also invalidate this
 * cache, keeping editor/detail and app-area consumers aligned with the
 * canonical launch projection used by the overview.
 */
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor';
import { type AppBasesQuery, appBasesQuery } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { useEffect } from 'react';
import type { SWRResponse } from 'swr';
import useSWR from 'swr';

import {
  FEATURE_KEY_APP_BASES_CREATE,
  useFeatureGate,
} from './use-feature-gate';

export type AppBaseInfo = AppBasesQuery['appBases'][number];

export interface UseWorkspaceAppBasesResult {
  views: AppBaseInfo[] | undefined;
  isLoading: boolean;
  isValidating: boolean;
  error: GraphQLError | undefined;
  mutate: SWRResponse<AppBaseInfo[], GraphQLError>['mutate'];
}

const SERVER_PROVISIONING_REFRESH_INTERVAL_MS = 500;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function hasPendingServerProvisioning(view: AppBaseInfo): boolean {
  const config = asRecord(view.config);
  const provisioning = asRecord(config?.provisioning);
  return provisioning?.status === 'pending';
}

export function useWorkspaceAppBases(
  workspaceId: string | null
): UseWorkspaceAppBasesResult {
  const graphql = useService(GraphQLService);
  const workspaceRealtimeService = useService(WorkspaceRealtimeService);
  const { isFeatureAllowed, isLoading: isFeatureGateLoading } =
    useFeatureGate();
  const canQueryAppBases =
    !isFeatureGateLoading && isFeatureAllowed(FEATURE_KEY_APP_BASES_CREATE);

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    AppBaseInfo[],
    GraphQLError
  >(
    workspaceId && canQueryAppBases ? ['appBases', workspaceId] : null,
    async () => {
      const res = await graphql.gql({
        query: appBasesQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return res.appBases;
    },
    {
      // Global SWRConfig sets suspense:true — opt-out so the list-page can
      // render its own loading/error UI instead of suspending the route.
      suspense: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: latestViews =>
        latestViews?.some(hasPendingServerProvisioning)
          ? SERVER_PROVISIONING_REFRESH_INTERVAL_MS
          : 0,
      shouldRetryOnError: error => !isFeatureUnavailableError(error),
    }
  );

  useEffect(() => {
    if (!workspaceId || !canQueryAppBases) return;
    const releaseWorkspacePresence =
      workspaceRealtimeService.retainWorkspacePresence();
    const subscription =
      workspaceRealtimeService.workspaceMetaChanged$.subscribe(event => {
        if (
          event &&
          event.workspaceId === workspaceId &&
          event.changeType.startsWith('app_base_')
        ) {
          void mutate();
        }
      });
    return () => {
      subscription.unsubscribe();
      releaseWorkspacePresence();
    };
  }, [canQueryAppBases, mutate, workspaceId, workspaceRealtimeService]);

  return {
    views: data,
    isLoading: canQueryAppBases ? isLoading : isFeatureGateLoading,
    isValidating,
    error: isFeatureUnavailableError(error) ? undefined : error,
    mutate,
  };
}

function isFeatureUnavailableError(error: unknown): boolean {
  if (!error) return false;
  const code = (error as { extensions?: { code?: unknown } }).extensions?.code;
  if (code === 'FEATURE_NOT_AVAILABLE') return true;

  const graphQLErrors = (
    error as
      | {
          graphQLErrors?: Array<{
            extensions?: { code?: unknown };
          }>;
        }
      | undefined
  )?.graphQLErrors;
  return (
    graphQLErrors?.some(
      graphQLError => graphQLError.extensions?.code === 'FEATURE_NOT_AVAILABLE'
    ) ?? false
  );
}
